import * as React from 'react';
import { connect } from 'react-redux';
import { StandardPageLayout } from 'ui/components/StandardPageLayout';
import { EnableLxLoginEnum, LxConfig, ROUTES } from '../../../global/constants';
import { Redirect } from 'react-router';
import bind from 'bind-decorator';
import { showErrorMessage } from 'ui/components';
import * as LoginActions from 'auth/actions';
import { RootState } from 'global/state';
import { getLoggedInStatus } from 'auth/selectors';
import { authLocalStorageManager } from 'auth/utils';
import { SignupBirthdate } from 'user/components/UserRegistration/SignupBirthdate';
import { SignupDenied, SignupDeniedReason } from 'user/components/UserRegistration/SignupDenied';
import { About } from 'home/components';

interface Props {
    isLoggedIn: boolean;
    loginUser: typeof LoginActions.login;
}

enum Step {
    AskBirthdate = 0,
    CreateYourAccount,
    RegistrationDenied,
}

interface State {
    email: string;
    password: string;
    step: Step;
    cancel: boolean;
}



/*
 * Sign up form used to login with email/password on LX.
 * This uses the same steps logic of the old SignupPage.tsx
 */
@connect((state: RootState) => ({
    isLoggedIn: getLoggedInStatus(state),
}), {
    loginUser: LoginActions.login,
})
export class LXSignUpView extends React.PureComponent<Props, State> {

    constructor(props: any) {
        super(props);
        this.state = {
            email: '',
            password: '',
            step: Step.AskBirthdate,
            cancel: false,
        };
    }

    public getComponent() {
        switch (this.state.step) {
            /// TODO We need to change this logic to allow users under 18 years old
            case Step.AskBirthdate: {
                return (
                    <StandardPageLayout>
                        <SignupBirthdate
                            onCancel={() => this.onCancel()}
                            onSuccess={() => this.setState({step: Step.CreateYourAccount})}
                            onFailure={() => this.setState({step: Step.RegistrationDenied})}
                        />
                    </StandardPageLayout>
                );
            }
            case Step.CreateYourAccount: {
                return (
                    <StandardPageLayout>
                        <form onSubmit={this.onSubmit} method='POST'>
                            <label>
                            Email:
                            <input name='email' type='text' value={this.state.email} onChange={this.onEmailChange} />
                            </label><br/>
                            <label>
                            Password (min 8 characters, with uppercase, lowercase, number and special character):
                            <input name='password' type='password' value={this.state.password} onChange={this.onPasswordChange} />
                            </label><br/>
                            <input type='submit' value='Submit' />
                        </form>
                    </StandardPageLayout>
                );
            }
            case Step.RegistrationDenied: {
                return (<SignupDenied reason={SignupDeniedReason.AGE} />);
            }
        }
    }

    public render(){
        if (this.state.cancel) {
            return <About />;
        }
        if ((this.props.isLoggedIn && authLocalStorageManager.userBirthdate !== null) ||
                !this.isLxLoginActive()) {
            return <Redirect to={ROUTES.Explore.HOME} push={true} />;
        }
        return this.getComponent();
    }

    @bind private isLxLoginActive() {
        return LxConfig.EnableLxLogin === EnableLxLoginEnum.Debug ||
                LxConfig.EnableLxLogin === EnableLxLoginEnum.Production;
    }

    @bind private onEmailChange(event: any) {
        this.setState({ email: event.target.value });
    }

    @bind private onPasswordChange(event: any) {
        this.setState({ password: event.target.value });
    }

    @bind private onCancel() {
        this.setState({ cancel: true });
    }

    @bind private async onSubmit(event: any) {
        event.preventDefault();

        /// Build data for request
        const formData  = new FormData();
        formData.append('email', this.state.email);
        formData.append('password', this.state.password);

        let response;

        try {
            /// This response login the user on django and returns
            /// a `Set-Cookie` header to set the `labxchange_sessionid` cookie.
            ///
            /// By default the `Set-Cookie` doesn't works. It is nedeed the `credentials`
            /// param for it to work and save the cookie.
            /// Ref: https://stackoverflow.com/a/46481100
            response = await fetch(`${LxConfig.BackendBaseUrl}/api/v1/users/sign_up/`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
        }
        catch {
            /// When the servers returns an 500 error the frontend generates a CORS error
            /// because the backend does not send the Access-Control-Allow-Credentials header
            /// when unexpected errors occur
            showErrorMessage('An error occurred during login');
            return;
        }

        // TODO intl of the messages
        if (response.status === 200) {
            /// After the `labxchange_sessionid` cookie is set,
            /// we redirect to `/oauth2/authorize` to complete the login
            this.props.loginUser();
        }
        else if (response.status === 401) {
            /// Email/Password validation errors
            const errorMessage = (await response.json()).error_message;
            if (errorMessage) {
                showErrorMessage(errorMessage);
            }
            else {
                showErrorMessage('Unable to login');
            }
        }
        else if (response.status === 429) {
            /// Rate limit reached
            showErrorMessage('Too many failed attempts. Try again later');
        }
        else {
            showErrorMessage('Unable to login');
        }
    }
}
